import { Mutex } from 'async-mutex';

const mutex = new Mutex();

const closeError = new Error('WebSocket was closed');
const CONNECTION_TIMEOUT = 3000;
const MAX_TIMEOUT = 30000;
const PUBLIC_ANDROID_HANDOFF_RETURN_SCHEME = 'runai-public://public-handoff';

function shouldClaimPreconnectedSocket() {
  if (typeof window === 'undefined') return true;

  try {
    const search = window.location?.search || '';
    const searchParams = new URLSearchParams(search);
    return !(
      searchParams.get('run_source') === 'saas'
      && searchParams.get('run_return_to') === PUBLIC_ANDROID_HANDOFF_RETURN_SCHEME
    );
  } catch {
    return true;
  }
}

export default class PromisedWebSockets {
  private closed: boolean;

  private timeout: number;

  private stream: Buffer;

  private canRead?: boolean | Promise<boolean>;

  private resolveRead: ((value?: any) => void) | undefined;

  private client: WebSocket | undefined;

  private website?: string;

  private disconnectedCallback: () => void;

  private offlineHandler: (() => void) | undefined;

  private readFailureInjector?: () => Error | undefined;

  constructor(disconnectedCallback: () => void) {
    this.client = undefined;
    this.closed = true;
    this.stream = Buffer.alloc(0);
    this.disconnectedCallback = disconnectedCallback;
    this.timeout = CONNECTION_TIMEOUT;
  }

  async readExactly(number: number) {
    let readData = Buffer.alloc(0);

    while (true) {
      this.throwIfReadFailureInjected();
      if (this.closed) {
        throw closeError;
      }

      // Promise.race ensures we don't hang if read() never resolves
      const thisTime = await Promise.race([
        this.read(number),
        new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('WebSocket read timeout')), MAX_TIMEOUT);
        }),
      ]);

      if (thisTime.length === 0) {
        // Avoid tight loop on zero-length reads (stream drained by concurrent reader)
        await new Promise((resolve) => {
          setTimeout(resolve, 100);
        });
        continue;
      }

      readData = Buffer.concat([readData, thisTime]);
      number -= thisTime.length;
      if (!number) {
        return readData;
      }
    }
  }

  async read(number: number) {
    this.throwIfReadFailureInjected();
    if (this.closed) {
      throw closeError;
    }
    await this.canRead;
    if (this.closed) {
      throw closeError;
    }
    const toReturn = this.stream.slice(0, number);
    this.stream = this.stream.slice(number);
    if (this.stream.length === 0) {
      this.canRead = new Promise((resolve) => {
        this.resolveRead = resolve;
      });
    }

    return toReturn;
  }

  async readAll() {
    if (this.closed || !await this.canRead) {
      throw closeError;
    }
    const toReturn = this.stream;
    this.stream = Buffer.alloc(0);
    this.canRead = new Promise((resolve) => {
      this.resolveRead = resolve;
    });

    return toReturn;
  }

  getWebSocketLink(ip: string, port: number, isTestServer?: boolean, isPremium?: boolean) {
    if (port === 443) {
      return `wss://${ip}/apiws${isTestServer ? '_test' : ''}${isPremium ? '_premium' : ''}`;
    } else if (port === 80) {
      return `ws://${ip}/apiws${isTestServer ? '_test' : ''}${isPremium ? '_premium' : ''}`;
    } else {
      return `ws://${ip}:${port}/apiws${isTestServer ? '_test' : ''}${isPremium ? '_premium' : ''}`;
    }
  }

  connect(port: number, ip: string, isTestServer = false, isPremium = false) {
    this.stream = Buffer.alloc(0);
    this.canRead = new Promise((resolve) => {
      this.resolveRead = resolve;
    });
    this.closed = false;
    this.website = this.getWebSocketLink(ip, port, isTestServer, isPremium);
    // Reuse preconnected WebSocket from index.html if available (saves ~200ms on first load)
    const preWs = typeof window !== 'undefined' && (window as any).__preconnectedWs;
    if (shouldClaimPreconnectedSocket()
      && preWs
      && preWs.url === this.website
      && preWs.readyState <= WebSocket.OPEN) {
      this.client = preWs;
      (window as any).__preconnectedWs = null; // Claim it
    } else {
      this.client = new WebSocket(this.website, 'binary');
    }
    return new Promise((resolve, reject) => {
      const client = this.client;
      if (!client) return;
      let hasResolved = false;
      let timeout: ReturnType<typeof globalThis.setTimeout> | undefined;
      const resolveConnected = () => {
        if (this.client !== client) {
          return;
        }
        this.receive(client);
        resolve(this);
        hasResolved = true;
        if (timeout) clearTimeout(timeout);
      };
      client.onopen = () => {
        resolveConnected();
      };
      client.onerror = (error) => {
        if (this.client !== client) {
          return;
        }
        // eslint-disable-next-line no-console
        console.warn('WebSocket error', error);
        reject(error);
        hasResolved = true;
        if (timeout) clearTimeout(timeout);
      };
      client.onclose = (event) => {
        if (this.client !== client) {
          return;
        }
        const { code, reason, wasClean } = event;
        if (code !== 1000 && !wasClean) {
          // eslint-disable-next-line no-console
          console.warn(`Socket ${ip} closed. Code: ${code}, reason: ${reason}`);
        }

        this.resolveRead?.(false);
        this.closed = true;
        if (this.disconnectedCallback) {
          this.disconnectedCallback();
        }
        hasResolved = true;
        if (timeout) clearTimeout(timeout);
      };

      timeout = setTimeout(() => {
        if (hasResolved || this.client !== client) return;

        reject(new Error('WebSocket connection timeout'));
        this.resolveRead?.(false);
        this.closed = true;
        if (this.disconnectedCallback) {
          this.disconnectedCallback();
        }
        client.close();
        this.timeout *= 2;
        this.timeout = Math.min(this.timeout, MAX_TIMEOUT);
        timeout = undefined;
      }, this.timeout);

      // Remove previous offline handler to prevent listener accumulation
      if (this.offlineHandler) {
        self.removeEventListener('offline', this.offlineHandler);
      }
      this.offlineHandler = () => {
        this.close();
        this.resolveRead?.(false);
      };
      self.addEventListener('offline', this.offlineHandler);

      if (client.readyState === WebSocket.OPEN) {
        resolveConnected();
      }
    });
  }

  write(data: Buffer<ArrayBuffer>) {
    if (this.closed) {
      throw closeError;
    }
    this.client?.send(data);
  }

  close() {
    this.client?.close();
    this.closed = true;
  }

  setReadFailureInjector(readFailureInjector?: () => Error | undefined) {
    this.readFailureInjector = readFailureInjector;
  }

  private throwIfReadFailureInjected() {
    const injectedError = this.readFailureInjector?.();
    if (injectedError) {
      throw injectedError;
    }
  }

  receive(client = this.client) {
    if (!client) return;
    client.onmessage = async (message) => {
      if (this.client !== client) {
        return;
      }
      await mutex.runExclusive(async () => {
        const data = message.data instanceof ArrayBuffer
          ? Buffer.from(message.data)
          : Buffer.from(await new Response(message.data).arrayBuffer());
        this.stream = Buffer.concat([this.stream, data]);
        this.resolveRead?.(true);
      });
    };
  }
}
